import { useEffect, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { http, registerAuthHandlers, tokenStorage } from '@shared/api';
import { decodeJwt } from '@shared/lib/jwt';
import { ROUTES } from '@shared/config/routes';
import { useSessionStore, type AdminIdentity } from '@entities/session';
import { refreshTokens } from '@features/auth';

async function resolveIdentity(): Promise<AdminIdentity | null> {
  const access = tokenStorage.getAccess();
  if (!access) return null;
  const sub = decodeJwt(access)?.sub;
  if (!sub) return null;
  try {
    const basic = await http
      .get(`users/${sub}/basic`)
      .json<{ id: string; email: string | null; displayName: string }>();
    return {
      id: basic.id,
      email: basic.email ?? '',
      displayName: basic.displayName,
    };
  } catch {
    return { id: sub, email: '', displayName: null };
  }
}

export function AuthBootstrap({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const hydrate = useSessionStore((s) => s.hydrate);

  useEffect(() => {
    hydrate();
    registerAuthHandlers({
      onRefresh: refreshTokens,
      onUnauthorized: () => {
        useSessionStore.getState().clear();
        navigate(ROUTES.login, { replace: true });
      },
    });

    // If we have a valid session but no identity yet, fetch it.
    const { status, identity, setIdentity } = useSessionStore.getState();
    if (status === 'authenticated' && !identity) {
      void resolveIdentity().then((id) => {
        if (id) setIdentity(id);
      });
    }
  }, [hydrate, navigate]);

  return <>{children}</>;
}
